/**
 * §06 — Weapon scheduling and fire resolution for all 12 weapons and their 12 evolutions.
 *
 * Step 8 of the sim order: cooldowns advance, fire events resolve, projectiles spawn.
 * Damage is snapshotted at fire time (§4.1) and the crit roll for a strike event is taken here,
 * from `w.rng.crit`, in §4.11 pipeline order. Persistent shapes (orbitals, beams, minions,
 * zones, mines) are reconciled against their stat `count` every tick rather than re-spawned.
 */

import { MAX_WEAPON_LEVEL, weaponById } from '../core/content';
import { CRIT_MULT_BASE, CRIT_MULT_CAP } from '@/sim/damage';
import { buildExtras } from './build';
import type { Projectile } from './entities';
import type { World } from './world';
import {
  Behaviour,
  LEADER_FLAG,
  armVolley,
  coneDamage,
  projectileSlot,
  radialDamage,
  resetBeamRamp,
  resetSpawnStats,
  resolveChain,
  spawnProjectile,
  spawnStats,
  STEP_S,
} from './projectiles';
import {
  MAX_ACQUISITION_RANGE,
  POLICY_DENSEST_CLUSTER,
  POLICY_ELITE_PRIORITY,
  VISUAL_CLASS_BY_WEAPON,
  acquireTargetByCode,
  activeWeaponRuntime,
  aimAt,
  clampToVisible,
  aimOut,
  enemyByEntityId,
  weaponRuntime,
  type WeaponRuntime,
} from './targeting';
import { wrapDeltaX, wrapDeltaY } from './wrap';

export { VISUAL_CLASS_BY_WEAPON };

/** §4.3 — crit chance hard cap. */
export const CRIT_CHANCE_CAP = 0.75;
/** Slot index used for the active-ability weapon (weapon.gulper-maw). */
export const ACTIVE_SLOT = 4;
const SLOTS = 5;
const BEHAVIOURS = 12;

// --- module-scope state (allocated once) ------------------------------------------------------

const liveCount = new Int32Array(SLOTS * BEHAVIOURS);
const firstIndex = new Int32Array(SLOTS * BEHAVIOURS);
/** Minion respawn schedule per slot (§6.3.8 — 6 s after death). */
const respawnAt = new Int32Array(SLOTS);
/** Highest minion count reached per slot, so a drop is read as a death, not a first fill. */
const scionSeen = new Int32Array(SLOTS);
/** Armada volley schedule per slot (§6.3.8 evolution — 5 s cadence). */
const volleyAt = new Int32Array(SLOTS);
/** Krillstorm emitter-arm cursor and phase per slot (§6.3.2 evolution). */
const armCursor = new Int32Array(SLOTS);
/** Vampire-shroud state per slot: barrier readiness, refresh tick, stored damage. */
const barrierReady = new Int32Array(SLOTS);
const barrierRefreshAt = new Int32Array(SLOTS);
const storedDamage = new Float64Array(SLOTS);
const storedSince = new Int32Array(SLOTS);
let lastPlayerHp = -1;

function key(slot: number, behaviour: number): number {
  return slot * BEHAVIOURS + behaviour;
}

/** Reset every per-run weapon schedule. Called on tick 0 and by the integrator on run reset. */
export function resetWeapons(): void {
  liveCount.fill(0);
  firstIndex.fill(-1);
  respawnAt.fill(0);
  scionSeen.fill(0);
  volleyAt.fill(0);
  armCursor.fill(0);
  barrierReady.fill(1);
  barrierRefreshAt.fill(0);
  storedDamage.fill(0);
  storedSince.fill(0);
  wasEvolved.fill(0);
  lastPlayerHp = -1;
}

// --- shared helpers ---------------------------------------------------------------------------

/**
 * §4.1 steps 1–2 (attacker side) — the value frozen into `projectile.damage_snapshot`.
 * Lumensurge, the target's damage-taken bonus, crit, mitigation and rounding are applied by
 * `applyDamageToEnemy` (R1) at hit time, so they are deliberately absent here.
 */
function snapshotDamage(w: World, base: number): number {
  return base * w.player.stats.damage_mult;
}

/**
 * §4.1 step 4 — one `crit`-stream roll, taken early only where the fire path needs the result
 * (evolution.kingspear-medusa fragments on crit). Every other weapon lets `applyDamageToEnemy`
 * take the roll at hit time so exactly one roll is consumed per hit event (§4.11).
 */
function rollCrit(w: World): boolean {
  const cc = Math.min(w.player.stats.crit_chance, CRIT_CHANCE_CAP);
  if (cc <= 0) return false;
  return w.rng.crit.next() < cc;
}

/** §4.2 — crit multiplier, matching the damage module's clamp. */
function critMultiplier(w: World): number {
  const m = w.player.stats.crit_mult;
  return m < CRIT_MULT_BASE ? CRIT_MULT_BASE : m > CRIT_MULT_CAP ? CRIT_MULT_CAP : m;
}

/** §4.6 — `cooldown = base / (1 + haste)`; `cooldown_mult` is the resolved 1/(1+haste). */
function cooldownTicks(w: World, seconds: number): number {
  const t = Math.round(seconds * 60 * w.player.stats.cooldown_mult);
  return t < 1 ? 1 : t;
}

function ratePeriod(rt: WeaponRuntime): number {
  if (rt.cast_cooldown_s > 0) return rt.cast_cooldown_s;
  if (rt.cooldown_s > 0) return rt.cooldown_s;
  return rt.rate > 0 ? 1 / rt.rate : 1;
}

function projectileCount(w: World, rt: WeaponRuntime): number {
  const n = Math.round(rt.count + w.player.stats.count_add);
  return n < 1 ? 1 : n;
}

/** §5.10 / §3.8 — the direction Focus Aim (or the movement heading) points. */
function fallbackDir(w: World, out: { x: number; y: number }): void {
  const p = w.player;
  if (p.focus_aim.held) {
    const l = Math.sqrt(p.focus_aim.dir.x * p.focus_aim.dir.x + p.focus_aim.dir.y * p.focus_aim.dir.y);
    if (l > 1e-6) {
      out.x = p.focus_aim.dir.x / l;
      out.y = p.focus_aim.dir.y / l;
      return;
    }
  }
  const vl = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y);
  if (vl > 1e-3) {
    out.x = p.vel.x / vl;
    out.y = p.vel.y / vl;
    return;
  }
  out.x = 1;
  out.y = 0;
}

const dirScratch = { x: 1, y: 0 };

/**
 * §6.6 — which bolt of a volley carries the cascade charge.
 *
 * One per volley, not one per bolt, and that single decision is what keeps the cascade off the
 * geometric path: a Krillscatter fan is 12 pellets and 12 cascades a volley would be 120 damage
 * events for one trigger pull. The middle bolt gets it because that is the one aimed at what the
 * targeting system picked — the outer pellets of a fan are the ones that miss.
 */
function cascadeBolt(count: number): number {
  return count >> 1;
}

/** §5.2/§6 — evolution gate: weapon at max level AND its linked passive owned (spine §5). */
function evolutionReady(w: World, defId: string): boolean {
  const def = weaponById.get(defId);
  if (!def) return false;
  const linked = typeof def.linked_passive_id === 'string' ? def.linked_passive_id : '';
  if (linked === '' || typeof def.evolution_id !== 'string') return false;
  const p = w.player;
  for (let i = 0; i < p.passive_count && i < p.passives.length; i++) {
    if (p.passives[i].def_id === linked && p.passives[i].rank > 0) return true;
  }
  return false;
}

/**
 * §6.1 / spine §5 — evolution is gated on weapon max level AND its linked passive owned.
 * §09 (`progression.ts`) grants it as a level-up choice and sets `WeaponSlot.evolved`; this
 * module reacts to that flip. `setAutoEvolve(true)` makes the gate self-firing instead, which
 * headless balance tests use to reach evolved DPS without driving the level-up UI.
 */
let autoEvolve = false;
export function setAutoEvolve(on: boolean): void {
  autoEvolve = on;
}

/** Last-seen evolved flag per slot, so the transition can rebuild persistent shapes once. */
const wasEvolved = new Int32Array(SLOTS);

function checkEvolutions(w: World): void {
  const p = w.player;
  for (let s = 0; s < p.weapon_count && s < 4; s++) {
    const slot = p.weapons[s];
    if (slot.def_id === '') continue;
    if (
      autoEvolve &&
      !slot.evolved &&
      slot.level >= MAX_WEAPON_LEVEL &&
      evolutionReady(w, slot.def_id)
    ) {
      const def = weaponById.get(slot.def_id);
      slot.evolved = true;
      w.bus.emit('evolution', { weapon_id: slot.def_id, evolution_id: (def?.evolution_id as string) ?? '' });
    }
    const now = slot.evolved ? 1 : 0;
    if (wasEvolved[s] !== now) {
      wasEvolved[s] = now;
      releaseSlotPersistents(w, s);
    }
  }
}

/** Drop persistent shapes so the evolved stats rebuild them cleanly on the next tick. */
function releaseSlotPersistents(w: World, slot: number): void {
  const list = w.projectiles.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const p = w.projectiles.slots[list[i]];
    if (!p.alive) continue;
    if (projectileSlot(p) !== slot) continue;
    if (
      p.behaviour === Behaviour.Orbital ||
      p.behaviour === Behaviour.Beam ||
      p.behaviour === Behaviour.Minion
    ) {
      w.projectiles.release(p);
    }
  }
}

// --- census ------------------------------------------------------------------------------------

function census(w: World): void {
  liveCount.fill(0);
  firstIndex.fill(-1);
  const list = w.projectiles.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const p = w.projectiles.slots[list[i]];
    if (!p.alive) continue;
    const s = projectileSlot(p);
    if (s < 0 || s >= SLOTS) continue;
    const k = key(s, p.behaviour);
    if (firstIndex[k] < 0) firstIndex[k] = p.pool_index;
    liveCount[k]++;
  }
}

// --- step 8 --------------------------------------------------------------------------------------

/** Step 8 — cooldowns, fire resolution, per-weapon spawn patterns. */
export function stepWeapons(w: World): void {
  if (w.tick === 0) resetWeapons();
  if (lastPlayerHp < 0) lastPlayerHp = w.player.hp;

  checkEvolutions(w);
  census(w);

  const p = w.player;
  for (let s = 0; s < p.weapon_count && s < 4; s++) {
    const slot = p.weapons[s];
    if (slot.def_id === '') continue;
    const rt = weaponRuntime(w, s);
    if (!rt.valid) continue;
    switch (slot.def_id) {
      case 'weapon.spear-jelly-lance':
        fireLance(w, s, rt);
        break;
      case 'weapon.krill-scatter':
        fireKrill(w, s, rt);
        break;
      case 'weapon.barreleye-gaze':
        maintainLockBeam(w, s, rt);
        break;
      case 'weapon.copepod-halo':
        maintainOrbiters(w, s, rt);
        break;
      case 'weapon.atolla-nova':
        fireNova(w, s, rt);
        break;
      case 'weapon.angler-lantern-mine':
        deployMine(w, s, rt);
        break;
      case 'weapon.siphonophore-chain-whip':
        fireChain(w, s, rt);
        break;
      case 'weapon.bobtail-scion':
        maintainScions(w, s, rt);
        break;
      case 'weapon.brine-pool':
        castPool(w, s, rt);
        break;
      case 'weapon.vampire-shroud':
        runShroud(w, s, rt);
        break;
      case 'weapon.loosejaw-searchlight':
        maintainSweepBeams(w, s, rt);
        break;
      default:
        break;
    }
  }

  runActiveSlot(w);
  lastPlayerHp = p.hp;
}

// --- 6.3.1 spear-jelly lance -----------------------------------------------------------------------

function fireLance(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  if (w.tick < slot.cooldown_until) return;
  const target = enemyByEntityId(w, slot.last_target_id);
  if (target === null) return;

  const p = w.player;
  const speed = rt.speed * p.stats.speed_mult;
  aimAt(p.pos.x, p.pos.y, target, speed);
  const dx = aimOut.x;
  const dy = aimOut.y;
  // §6.3.1 evolution — the barb fragments need the crit result at fire time, so the evolved
  // lance pre-rolls and bakes the multiplier in; the base lance defers the roll to hit time.
  const preRoll = rt.evolved && rt.evo_barbs > 0;
  const crit = preRoll ? rollCrit(w) : false;
  const n = projectileCount(w, rt);

  resetSpawnStats();
  spawnStats.damage = snapshotDamage(w, rt.damage) * (crit ? critMultiplier(w) : 1);
  spawnStats.crit = crit;
  spawnStats.speed = speed;
  spawnStats.radius = Math.max(0.3, rt.radius * p.stats.area_mult);
  spawnStats.penetration = rt.penetration;
  spawnStats.duration_s = rt.duration_s * p.stats.duration_mult;
  spawnStats.behaviour = Behaviour.Linear;
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  spawnStats.phase = Math.atan2(dy, dx);
  spawnStats.knockback = rt.knockback;
  // §6.6 — every bolt can burst; only one can cascade.
  spawnStats.split = buildExtras.split_shards;
  const cascadeIndex = cascadeBolt(n);

  // §6.3.1 L5 — twin lance: parallel shots offset perpendicular to the aim line.
  for (let i = 0; i < n; i++) {
    const offset = n === 1 ? 0 : (i - (n - 1) / 2) * 0.9;
    const ox = p.pos.x - dy * offset;
    const oy = p.pos.y + dx * offset;
    spawnStats.cascade = i === cascadeIndex ? buildExtras.cascade_levels : 0;
    if (spawnProjectile(w, slot.def_id, ox, oy, dx, dy, spawnStats, p.id) === null) break;
  }
  slot.cooldown_until = w.tick + cooldownTicks(w, 1 / rt.rate);
}

// --- 6.3.2 krillscatter organ ----------------------------------------------------------------------

function fireKrill(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  const p = w.player;
  const arms = rt.evolved && rt.evo_arms > 0 ? rt.evo_arms | 0 : 1;
  const armRateMult = rt.evolved ? rt.evo_arm_rate_mult : 1;

  // §6.3.2 evolution — arms rotate continuously; the phase is sim state on the weapon slot.
  if (rt.evolved && rt.evo_arm_rotation > 0) {
    slot.phase += (rt.evo_arm_rotation * Math.PI * STEP_S) / 180;
    if (slot.phase > Math.PI * 2) slot.phase -= Math.PI * 2;
  }
  if (w.tick < slot.cooldown_until) return;

  const target = enemyByEntityId(w, slot.last_target_id);
  // §6.3.2 — the fan opens with distance: the 28° cone is the spread at the weapon's max
  // range, which is what makes the ≥90% point-blank pellet-hit acceptance reachable.
  let openness = 1;
  if (target !== null) {
    const d = aimAt(p.pos.x, p.pos.y, target, rt.speed * p.stats.speed_mult);
    dirScratch.x = aimOut.x;
    dirScratch.y = aimOut.y;
    const reach = rt.range > 0 ? rt.range : 22;
    openness = d / reach;
    if (openness > 1) openness = 1;
  } else {
    fallbackDir(w, dirScratch);
  }
  let baseAngle = Math.atan2(dirScratch.y, dirScratch.x);
  if (arms > 1) {
    // fire one arm per cycle so two arms never resolve on the same frame (acceptance)
    baseAngle = slot.phase + (Math.PI * 2 * armCursor[s]) / arms;
    armCursor[s] = (armCursor[s] + 1) % arms;
  }

  const pellets = projectileCount(w, rt);
  const spread = (rt.spread_deg * Math.PI * openness) / 180;

  resetSpawnStats();
  spawnStats.damage = snapshotDamage(w, rt.damage);
  spawnStats.speed = rt.speed * p.stats.speed_mult;
  spawnStats.radius = 0.3;
  spawnStats.penetration = rt.penetration;
  spawnStats.ricochet = rt.evolved ? rt.ricochet : 0;
  spawnStats.duration_s = rt.duration_s * p.stats.duration_mult;
  spawnStats.behaviour = Behaviour.Linear;
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  // §6.6 — a fan is the worst case for both boons: every pellet can burst (the per-frame shard
  // budget in `projectiles.ts` is what bounds that), but only the middle one cascades.
  spawnStats.split = buildExtras.split_shards;
  const cascadeIndex = cascadeBolt(pellets);

  for (let i = 0; i < pellets; i++) {
    const t = pellets === 1 ? 0 : i / (pellets - 1) - 0.5;
    const a = baseAngle + t * spread;
    spawnStats.phase = a;
    spawnStats.cascade = i === cascadeIndex ? buildExtras.cascade_levels : 0;
    if (spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, Math.cos(a), Math.sin(a), spawnStats, p.id) === null) break;
  }
  slot.cooldown_until = w.tick + cooldownTicks(w, 1 / (rt.rate * armRateMult * arms));
}

// --- 6.3.3 barreleye gaze --------------------------------------------------------------------------

function maintainLockBeam(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  const p = w.player;
  const k = key(s, Behaviour.Beam);
  let beam: Projectile | null = firstIndex[k] >= 0 ? w.projectiles.slots[firstIndex[k]] : null;
  if (beam !== null && !beam.alive) beam = null;

  if (beam === null) {
    resetSpawnStats();
    spawnStats.behaviour = Behaviour.Beam;
    spawnStats.duration_s = -1;
    spawnStats.visual_class = rt.visual_class;
    spawnStats.slot = s;
    spawnStats.tick_s = 1 / rt.rate;
    beam = spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, 1, 0, spawnStats, p.id);
    if (beam === null) return;
  }
  if (beam.target_id !== slot.last_target_id) {
    beam.target_id = slot.last_target_id;
    resetBeamRamp(beam); // §6.3.3 evolution — ramp resets on retarget within one sim frame
  }
  beam.damage_snapshot = snapshotDamage(w, rt.damage);
  beam.hitbox_radius = (rt.width * p.stats.area_mult) / 2;
  beam.orbit_radius = rt.range;
  beam.penetration_left = rt.penetration > 0 ? rt.penetration : 1;
  beam.homing_strength = 0;
}

// --- 6.3.4 copepod halo ----------------------------------------------------------------------------

function maintainOrbiters(w: World, s: number, rt: WeaponRuntime): void {
  const p = w.player;
  const slot = w.player.weapons[s];
  const want = projectileCount(w, rt);
  const k = key(s, Behaviour.Orbital);
  const have = liveCount[k];
  const radius = rt.radius * p.stats.area_mult;
  const damage = snapshotDamage(w, rt.damage);

  const list = w.projectiles.liveIndices();
  let seen = 0;
  for (let i = 0; i < list.length; i++) {
    const q = w.projectiles.slots[list[i]];
    if (!q.alive || q.behaviour !== Behaviour.Orbital || projectileSlot(q) !== s) continue;
    if (seen >= want) {
      w.projectiles.release(q);
      continue;
    }
    q.damage_snapshot = damage;
    q.orbit_radius = radius;
    q.hitbox_radius = 0.7;
    seen++;
  }

  for (let i = have; i < want; i++) {
    resetSpawnStats();
    spawnStats.behaviour = Behaviour.Orbital;
    spawnStats.duration_s = -1;
    spawnStats.damage = damage;
    spawnStats.orbit_radius = radius;
    spawnStats.radius = 0.7;
    spawnStats.visual_class = rt.visual_class;
    spawnStats.slot = s;
    spawnStats.phase = (Math.PI * 2 * i) / want;
    spawnStats.knockback = rt.knockback;
    spawnStats.next_behaviour = i === 0 ? LEADER_FLAG : 0;
    spawnStats.tick_s = rt.evolved && rt.evo_flare_interval_s > 0 ? rt.evo_flare_interval_s : 0;
    if (spawnProjectile(w, slot.def_id, p.pos.x + radius, p.pos.y, 1, 0, spawnStats, p.id) === null) break;
  }
}

// --- 6.3.5 atolla nova ------------------------------------------------------------------------------

function fireNova(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  if (w.tick < slot.cooldown_until) return;
  const p = w.player;
  resetSpawnStats();
  spawnStats.behaviour = Behaviour.Aura;
  spawnStats.damage = snapshotDamage(w, rt.damage);
  // §6.3.5 counter-synergy — area scaling applies at 50% efficiency to the nova.
  spawnStats.area = rt.radius * (1 + (p.stats.area_mult - 1) * 0.5);
  spawnStats.radius = 0.5;
  spawnStats.knockback = rt.knockback;
  spawnStats.penetration = -1;
  spawnStats.duration_s = Math.max(0.1, spawnStats.area / Math.max(1, rt.speed));
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, 1, 0, spawnStats, p.id);
  slot.cooldown_until = w.tick + cooldownTicks(w, 1 / rt.rate);
}

// --- 6.3.6 angler lantern mine -----------------------------------------------------------------------

function deployMine(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  if (w.tick < slot.cooldown_until) return;
  const armed = liveCount[key(s, Behaviour.Mine)] + liveCount[key(s, Behaviour.Lobbed)];
  if (armed >= projectileCount(w, rt)) return;

  const p = w.player;
  // §6.3.6 — the lob destination is the densest-cluster target; the evolution lobs past the
  // base weapon's reach. §5.8 holds both to the visible ring: a mine thrown at a cluster the
  // player cannot see spends the cooldown and shows nothing.
  const range = clampToVisible(rt.evolved ? MAX_ACQUISITION_RANGE : rt.range);
  const targetId = acquireTargetByCode(w, POLICY_DENSEST_CLUSTER, p.pos.x, p.pos.y, range, 0, 1, 0);
  const target = enemyByEntityId(w, targetId);
  const dx = target !== null ? target.pos.x : p.pos.x;
  const dy = target !== null ? target.pos.y : p.pos.y;

  // AMD-24 — the lob leaves the muzzle toward the nearest image of its anchor, so a cluster on
  // the far side of the seam is thrown through it rather than back across the whole arena.
  const ax = wrapDeltaX(dx - p.pos.x);
  const ay = wrapDeltaY(dy - p.pos.y);
  const d = Math.sqrt(ax * ax + ay * ay) || 1;

  resetSpawnStats();
  spawnStats.behaviour = Behaviour.Lobbed;
  spawnStats.next_behaviour = Behaviour.Mine;
  spawnStats.damage = snapshotDamage(w, rt.damage);
  spawnStats.speed = rt.speed * p.stats.speed_mult;
  spawnStats.radius = 0.6;
  spawnStats.area = rt.radius * p.stats.area_mult;
  spawnStats.penetration = -1;
  spawnStats.duration_s = rt.duration_s * p.stats.duration_mult;
  spawnStats.arm_s = rt.arm_time_s;
  spawnStats.dest_x = dx;
  spawnStats.dest_y = dy;
  spawnStats.knockback = rt.knockback;
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  if (spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, ax / d, ay / d, spawnStats, p.id) === null) return;
  slot.cooldown_until = w.tick + cooldownTicks(w, ratePeriod(rt));
}

// --- 6.3.7 siphonophore chain whip --------------------------------------------------------------------

function fireChain(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  if (w.tick < slot.cooldown_until) return;
  const primaryId = slot.last_target_id;
  if (enemyByEntityId(w, primaryId) === null) return;

  const p = w.player;
  const damage = snapshotDamage(w, rt.damage);
  const linkRange = rt.range * p.stats.area_mult;
  resolveChain(w, primaryId, damage, rt.chain_targets, linkRange, rt.chain_falloff, slot.def_id, true, p.id, 0, 0);

  if (rt.evolved) {
    // §6.3.7 evolution — the primary arc forks into 2 branches at 60% ...
    const branches = rt.evo_fork_branches | 0;
    for (let b = 0; b < branches; b++) {
      const forkId = acquireTargetByCode(w, POLICY_ELITE_PRIORITY, p.pos.x, p.pos.y, rt.range, primaryId, 1, 0);
      if (forkId === 0 || forkId === primaryId) break;
      resolveChain(
        w,
        forkId,
        damage * (rt.evo_fork_damage_pct / 100),
        rt.chain_targets,
        linkRange,
        rt.chain_falloff,
        slot.def_id,
        true,
        p.id,
        primaryId,
        1
      );
    }
    // ... and struck enemies become re-arcing nodes for `node_duration_s`.
    if (rt.evo_node_duration_s > 0) {
      resetSpawnStats();
      spawnStats.behaviour = Behaviour.Chain;
      spawnStats.damage = damage * (rt.evo_node_damage_pct / 100);
      spawnStats.duration_s = rt.evo_node_duration_s * p.stats.duration_mult;
      spawnStats.target_id = primaryId;
      spawnStats.visual_class = rt.visual_class;
      spawnStats.slot = s;
      spawnStats.tick_s = 1;
      spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, 1, 0, spawnStats, p.id);
    }
  }
  slot.cooldown_until = w.tick + cooldownTicks(w, 1 / rt.rate);
}

// --- 6.3.8 bobtail scion --------------------------------------------------------------------------------

function maintainScions(w: World, s: number, rt: WeaponRuntime): void {
  const p = w.player;
  const slot = w.player.weapons[s];
  const want = Math.round(rt.count);
  const k = key(s, Behaviour.Minion);
  const have = liveCount[k];
  const damage = snapshotDamage(w, rt.damage);

  const list = w.projectiles.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const q = w.projectiles.slots[list[i]];
    if (!q.alive || q.behaviour !== Behaviour.Minion || projectileSlot(q) !== s) continue;
    q.damage_snapshot = damage;
  }

  if (have < want) {
    // §6.3.8 — only a *lost* scion waits `respawn_s`; the initial fill and a count upgrade
    // from a level-up materialize immediately.
    if (have < scionSeen[s]) {
      if (respawnAt[s] === 0) respawnAt[s] = w.tick + Math.round(rt.respawn_s * 60);
    } else {
      respawnAt[s] = 0;
    }
    if (w.tick >= respawnAt[s]) {
      respawnAt[s] = 0;
      scionSeen[s] = have + 1;
      resetSpawnStats();
      spawnStats.behaviour = Behaviour.Minion;
      spawnStats.duration_s = -1;
      spawnStats.damage = damage;
      spawnStats.speed = rt.speed;
      spawnStats.radius = Math.max(0.6, rt.radius);
      spawnStats.penetration = rt.penetration;
      spawnStats.visual_class = rt.visual_class;
      spawnStats.slot = s;
      const a = (Math.PI * 2 * have) / (want > 0 ? want : 1);
      spawnProjectile(
        w,
        slot.def_id,
        p.pos.x + Math.cos(a) * 2,
        p.pos.y + Math.sin(a) * 2,
        Math.cos(a),
        Math.sin(a),
        spawnStats,
        p.id
      );
    }
  } else {
    scionSeen[s] = want;
    let extra = have - want;
    for (let i = 0; i < list.length && extra > 0; i++) {
      const q = w.projectiles.slots[list[i]];
      if (!q.alive || q.behaviour !== Behaviour.Minion || projectileSlot(q) !== s) continue;
      w.projectiles.release(q);
      extra--;
    }
  }

  // §6.3.8 evolution — synchronized volley on the elite-priority target every 5 s.
  if (rt.evolved && rt.evo_volley_interval_s > 0) {
    if (volleyAt[s] === 0) volleyAt[s] = w.tick + Math.round(rt.evo_volley_interval_s * 60);
    if (w.tick >= volleyAt[s]) {
      volleyAt[s] = w.tick + Math.round(rt.evo_volley_interval_s * 60);
      const tgt = acquireTargetByCode(
        w,
        POLICY_ELITE_PRIORITY,
        p.pos.x,
        p.pos.y,
        clampToVisible(MAX_ACQUISITION_RANGE),
        0,
        1,
        0
      );
      if (tgt !== 0) {
        for (let i = 0; i < list.length; i++) {
          const q = w.projectiles.slots[list[i]];
          if (!q.alive || q.behaviour !== Behaviour.Minion || projectileSlot(q) !== s) continue;
          q.target_id = tgt;
          armVolley(q);
        }
      }
    }
  }
}

// --- 6.3.9 brine pool --------------------------------------------------------------------------------------

function castPool(w: World, s: number, rt: WeaponRuntime): void {
  const slot = w.player.weapons[s];
  if (w.tick < slot.cooldown_until) return;
  const pools = liveCount[key(s, Behaviour.Zone)] + liveCount[key(s, Behaviour.Lobbed)];
  if (pools >= Math.round(rt.count)) return;

  const p = w.player;
  const targetId = acquireTargetByCode(w, POLICY_DENSEST_CLUSTER, p.pos.x, p.pos.y, rt.range, 0, 1, 0);
  const target = enemyByEntityId(w, targetId);
  const dx = target !== null ? target.pos.x : p.pos.x;
  const dy = target !== null ? target.pos.y : p.pos.y;
  const ax = wrapDeltaX(dx - p.pos.x);
  const ay = wrapDeltaY(dy - p.pos.y);
  const d = Math.sqrt(ax * ax + ay * ay) || 1;

  resetSpawnStats();
  spawnStats.behaviour = Behaviour.Lobbed;
  spawnStats.next_behaviour = Behaviour.Zone;
  spawnStats.damage = snapshotDamage(w, rt.damage);
  spawnStats.crit = false; // §4.9 — zone ticks never crit
  spawnStats.speed = 24;
  spawnStats.radius = 0.5;
  spawnStats.area = rt.radius * p.stats.area_mult;
  spawnStats.penetration = -1;
  spawnStats.duration_s = rt.duration_s * p.stats.duration_mult;
  spawnStats.dest_x = dx;
  spawnStats.dest_y = dy;
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  if (spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, ax / d, ay / d, spawnStats, p.id) === null) return;
  slot.cooldown_until = w.tick + cooldownTicks(w, ratePeriod(rt));
}

// --- 6.3.10 vampire shroud ------------------------------------------------------------------------------------

function runShroud(w: World, s: number, rt: WeaponRuntime): void {
  const p = w.player;
  const cycleTicks = Math.max(1, Math.round(rt.duration_s * 60));
  if (barrierReady[s] === 0 && !rt.evolved && w.tick >= barrierRefreshAt[s]) {
    barrierReady[s] = 1; // §6.3.10 acceptance — refresh never overlaps an active barrier
  }
  if (rt.evolved) barrierReady[s] = 1; // persistent shroud, no cycle downtime

  const lost = lastPlayerHp - p.hp;
  if (lost > 0 && barrierReady[s] === 1) {
    const absorbed = Math.min(lost, rt.absorb);
    p.hp += absorbed;
    if (p.hp > p.max_hp) p.hp = p.max_hp;
    w.bus.emit('player_heal', { amount: absorbed, hp_after: p.hp });
    if (rt.evolved) {
      storedDamage[s] += absorbed;
      if (storedSince[s] === 0) storedSince[s] = w.tick;
    } else {
      barrierReady[s] = 0;
      barrierRefreshAt[s] = w.tick + cycleTicks;
      retaliate(w, s, rt);
    }
  }

  // §6.3.10 evolution — detonate stored ×1.5 (cap 150) at 100 stored or after 6 s.
  if (rt.evolved && storedDamage[s] > 0) {
    const timeUp = storedSince[s] !== 0 && w.tick - storedSince[s] >= Math.round(rt.evo_deton_after_s * 60);
    if (storedDamage[s] >= rt.evo_deton_trigger || timeUp) {
      const payload = Math.min(storedDamage[s] * rt.evo_deton_mult, rt.evo_stored_cap);
      storedDamage[s] = 0;
      storedSince[s] = 0;
      radialDamage(
        w,
        p.pos.x,
        p.pos.y,
        rt.radius * p.stats.area_mult,
        payload * p.stats.damage_mult * p.lumensurge_tier,
        w.player.weapons[s].def_id,
        true,
        p.id,
        rt.knockback,
        0
      );
    }
  }
}

function retaliate(w: World, s: number, rt: WeaponRuntime): void {
  const p = w.player;
  resetSpawnStats();
  spawnStats.behaviour = Behaviour.Aura;
  spawnStats.damage = snapshotDamage(w, rt.damage);
  spawnStats.area = rt.radius * p.stats.area_mult;
  spawnStats.radius = 0.5;
  spawnStats.knockback = rt.knockback;
  spawnStats.penetration = -1;
  spawnStats.duration_s = 0.25;
  spawnStats.visual_class = rt.visual_class;
  spawnStats.slot = s;
  spawnProjectile(w, w.player.weapons[s].def_id, p.pos.x, p.pos.y, 1, 0, spawnStats, p.id);
}

// --- 6.3.12 loosejaw searchlight ---------------------------------------------------------------------------------

function maintainSweepBeams(w: World, s: number, rt: WeaponRuntime): void {
  const p = w.player;
  const slot = w.player.weapons[s];
  const arms = rt.evolved && rt.evo_arms > 0 ? rt.evo_arms | 0 : 1;
  const k = key(s, Behaviour.Beam);
  const have = liveCount[k];
  const tickS = rt.contact_tick_s > 0 ? rt.contact_tick_s : 0.15;
  const perTick = snapshotDamage(w, rt.contact_dps * tickS);
  const rot = (Math.PI * 2) / (rt.revolution_s > 0 ? rt.revolution_s : 6);

  const list = w.projectiles.liveIndices();
  let idx = 0;
  for (let i = 0; i < list.length; i++) {
    const q = w.projectiles.slots[list[i]];
    if (!q.alive || q.behaviour !== Behaviour.Beam || projectileSlot(q) !== s) continue;
    if (idx >= arms) {
      w.projectiles.release(q);
      continue;
    }
    q.damage_snapshot = perTick;
    q.orbit_radius = rt.length;
    q.hitbox_radius = (rt.width * p.stats.area_mult) / 2;
    q.penetration_left = -1;
    // counter-rotating arms (§6.3.12 evolution)
    q.homing_strength = idx % 2 === 0 ? rot : -rot;
    idx++;
  }

  for (let i = have; i < arms; i++) {
    resetSpawnStats();
    spawnStats.behaviour = Behaviour.Beam;
    spawnStats.duration_s = -1;
    spawnStats.damage = perTick;
    spawnStats.crit = false; // continuous-contact model: no per-tick crit roll (§6.3.12)
    spawnStats.radius = (rt.width * p.stats.area_mult) / 2;
    spawnStats.orbit_radius = rt.length;
    spawnStats.penetration = -1;
    spawnStats.homing = i % 2 === 0 ? rot : -rot;
    spawnStats.phase = (Math.PI * 2 * i) / arms;
    spawnStats.visual_class = rt.visual_class;
    spawnStats.slot = s;
    spawnStats.tick_s = tickS;
    if (spawnProjectile(w, slot.def_id, p.pos.x, p.pos.y, 1, 0, spawnStats, p.id) === null) break;
  }
}

// --- 6.3.11 gulper maw (active slot) --------------------------------------------------------------------------------

function runActiveSlot(w: World): void {
  const p = w.player;
  const active = p.active;
  if (active === null || active.def_id !== 'weapon.gulper-maw') return;
  // `Player.active` carries no level field (A.4); §09 owns active-item levelling, so the
  // runtime resolves at max level until that field exists.
  const evolved = evolutionReady(w, 'weapon.gulper-maw');
  const rt = activeWeaponRuntime('weapon.gulper-maw', MAX_WEAPON_LEVEL, evolved);
  if (!rt.valid) return;
  if (!w.input.dashPressed) return;
  if (w.tick < active.cooldown_until) return;

  fallbackDir(w, dirScratch);
  const dx = dirScratch.x;
  const dy = dirScratch.y;

  const damage = snapshotDamage(w, rt.damage);
  const executed = coneDamage(
    w,
    p.pos.x,
    p.pos.y,
    dx,
    dy,
    rt.lunge_u,
    rt.cone_deg,
    damage,
    'weapon.gulper-maw',
    true,
    p.id,
    evolved ? rt.evo_devour_hp_pct : 0
  );

  // i-frames and the lunge itself stay in R1's dash state so §03.4 owns one implementation.
  p.dash.active = true;
  p.dash.dir.x = dx;
  p.dash.dir.y = dy;
  p.dash.end_tick = w.tick + Math.max(1, Math.round(rt.duration_s * 60));
  p.dash.iframes_until = w.tick + Math.round(rt.iframes_s * 60);

  let cd = rt.cooldown_s;
  if (evolved && executed > 0) {
    const heal = Math.min(executed * rt.evo_devour_heal, rt.evo_devour_heal_cap);
    if (heal > 0) {
      p.hp = Math.min(p.max_hp, p.hp + heal);
      w.bus.emit('player_heal', { amount: heal, hp_after: p.hp });
    }
    cd = Math.max(rt.evo_cd_floor_s, cd - executed * rt.evo_cdr_per_devour_s);
  }
  active.cooldown_until = w.tick + cooldownTicks(w, cd);
  p.dash.cooldown_until = active.cooldown_until;
}
